export interface EventsMap {
  [event: string]: any
}

export interface DefaultEvents extends EventsMap {
  [event: string]: (...args: any) => void
}

export interface Unsubscribe {
  (): void
}

export class Emitter<TEvents extends EventsMap = DefaultEvents> {
  private events = {} as Partial<{
    [Key in keyof TEvents]: Map<string, { i: string; cb: TEvents[Key] }>
  }>

  emit<K extends keyof TEvents>(this: this, event: K, ...args: Parameters<TEvents[K]>) {
    if (this.events[event]) {
      this.events[event].forEach(({ cb }) => cb(...args))
    }
  }

  on<K extends keyof TEvents>(event: K, cb: TEvents[K]): Unsubscribe
  on<K extends keyof TEvents>(event: K, id: string, cb: TEvents[K]): Unsubscribe
  on<K extends keyof TEvents>(
    event: K,
    idOrCallback: string | TEvents[K],
    callback?: TEvents[K],
  ): Unsubscribe {
    const [providedId, cb] =
      typeof idOrCallback === 'string' ? [idOrCallback, callback!] : [nextId(), idOrCallback]
    const internalId = nextId()

    this.events[event] ||= new Map()
    this.events[event].set(providedId, { i: internalId, cb })

    return () => {
      if (this.events[event]?.get(providedId)?.i === internalId) {
        this.events[event]?.delete(providedId)
      }
    }
  }

  once<K extends keyof TEvents>(event: K, cb: TEvents[K]): Unsubscribe
  once<K extends keyof TEvents>(event: K, id: string, cb: TEvents[K]): Unsubscribe
  once<K extends keyof TEvents>(
    event: K,
    idOrCallback: string | TEvents[K],
    callback?: TEvents[K],
  ): Unsubscribe {
    const [id, cb] =
      typeof idOrCallback === 'string' ? [idOrCallback, callback!] : [nextId(), idOrCallback]

    const unsubscribe = this.on(
      event,
      id,
      // @ts-ignore
      (...args: any[]) => {
        unsubscribe()
        cb(...args)
      },
    )

    return unsubscribe
  }

  makeListener<K extends keyof TEvents>(event: K) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const emitter = this

    function listener(callback: TEvents[K]): Unsubscribe
    function listener(idOrCallback: string | TEvents[K], callback?: TEvents[K]): Unsubscribe
    function listener(idOrCallback: string | TEvents[K], callback?: TEvents[K]): Unsubscribe {
      return emitter.on(event, idOrCallback, callback!)
    }

    return listener
  }
}

let id = new Date().getTime()
const nextId = () => `${++id}`
